import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const LoginForm = ({ onForgotPassword }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    const { data, error } = await signIn(formData?.email, formData?.password);

    if (error) {
      const isUnconfirmed =
        error?.message?.toLowerCase()?.includes('email not confirmed') ||
        error?.message?.toLowerCase()?.includes('email_not_confirmed');
      setErrors({
        submit: isUnconfirmed
          ? 'Please confirm your email address before signing in. Check your inbox for the confirmation link.'
          : error?.message || 'Invalid email or password. Please try again.'
      });
      setLoading(false);
      return;
    }

    // Role-based redirect using stored role only
    const storedRole =
      data?.user?.user_metadata?.role ||
      data?.user?.raw_user_meta_data?.role ||
      null;

    // Admin users skip verification
    if (storedRole === 'admin') {
      navigate('/admin-panel');
      return;
    }

    // Check profile verification status from user_profiles
    try {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('profile_verified')
        ?.eq('id', data?.user?.id)
        ?.single();

      if (profile?.profile_verified) {
        if (storedRole === 'brand') {
          navigate('/brand-dashboard');
        } else {
          navigate('/user-dashboard');
        }
      } else {
        navigate('/profile-verification-screen');
      }
    } catch {
      // Fallback: let AuthenticationGuard handle the redirect
      if (storedRole === 'brand') {
        navigate('/brand-dashboard');
      } else {
        navigate('/user-dashboard');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />
      <Input
        label="Password"
        type="password"
        name="password"
        placeholder="Enter your password"
        value={formData?.password}
        onChange={handleChange}
        error={errors?.password}
        required
      />
      <div className="flex items-center justify-between">
        <Checkbox
          label="Remember me"
          name="rememberMe"
          checked={formData?.rememberMe}
          onChange={handleChange}
        />
        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-primary hover:text-primary/80 transition-colors"
        >
          Forgot Password?
        </button>
      </div>
      {errors?.submit && (
        <div className="p-4 rounded-lg bg-error/10 border border-error/20">
          <p className="text-sm text-error">{errors?.submit}</p>
        </div>
      )}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        iconName="LogIn"
        iconPosition="right"
        className="h-12"
      >
        Sign In
      </Button>
    </form>
  );
};

export default LoginForm;